import { Helmet } from 'react-helmet-async';

/**
 * Declarative `<title>` / `<meta name="description">` for a page.
 *
 * Replaces the imperative `document.title = ...` +
 * `querySelector('meta[name="description"]').setAttribute(...)` effects that
 * `pages/issue` and `pages/article` each carried their own near-duplicate of.
 * Writing the DOM by hand from an effect has three problems the declarative
 * version doesn't:
 *
 * - **Nothing is ever restored.** The effects had no cleanup, so a title and
 *   description set while reading an article survived navigation back to the
 *   home screen until something else happened to overwrite them. Helmet
 *   unwinds its own stack on unmount.
 * - **`meta[name="description"]` had to already exist.** The old code did
 *   `querySelector(...)` and silently no-opped if the tag wasn't in
 *   `index.html`, which is tenant-templated (`index-template.ejs`). Helmet
 *   inserts the tag when it's missing.
 * - **It raced Helmet.** `pages/issue` already rendered a `<Helmet>` for the
 *   `og:` tags alongside these manual writes, so two mechanisms owned the
 *   same `<head>` with no defined ordering.
 *
 * Upgraded to `react-helmet-async` 3.x 2026-08-19 -- its peer range
 * (`^16.6.0 || ^17 || ^18 || ^19`) supports React 18 directly, so this
 * didn't have to wait for a React 19 migration after all. Still the one
 * file to touch if a future move to router-native head management (topic
 * 14) replaces it outright.
 */
export function DocumentHead({ title, description }: DocumentHeadProps) {
	// Rendering an empty <Helmet> is harmless, but skipping it entirely keeps
	// the head stack shorter on routes that have neither value resolved yet.
	if (!title && !description) {
		return null;
	}
	return (
		<Helmet>
			{title ? <title>{title}</title> : null}
			{description ? (
				<meta name="description" content={description} />
			) : null}
		</Helmet>
	);
}

export interface DocumentHeadProps {
	title?: string;
	description?: string;
}
